import React, { useState, useEffect, useRef } from 'react';

import LockerTableRow from './LockerTableRow';
import ToastNotification, { ToastMessageType} from  '@/components/elements/global/ToastNotification';
import FilterHeader from  '@/components/elements/global/FilterHeader';
import { LockerFile } from  '@/interfaces/LockerFile';
import useOnScreen from  '@/components/hooks/useOnScreen';
import { BuildPath } from  '@/helpers/Paths';
import { CancelConfirmModal } from 'thrive-components';

interface LockerFilesTableProps {
    lockerFiles: LockerFile[]
    userId: string
    filterOptions: {sort: string, sortBy:string},
    total: number,
    searchTerms: string,
    restoreOrDelete: (url: string, action: "restore" | "delete") => Promise<any>
    getLockerFiles: (type: 'refresh' | 'page') => void
    setFilterOptions: ({sort, sortBy}:{sort: string, sortBy:string}) => void
}

const LockerFilesTable = ({lockerFiles, userId, filterOptions, total, searchTerms, restoreOrDelete, getLockerFiles, setFilterOptions}: LockerFilesTableProps) => {
    
    /* Ref for infinite scrolling, if there are more files, load them and append to files when this comes into view */
    const scrollTo = useRef();
    const isVisible = useOnScreen(scrollTo, document.querySelector('.locker-details-table-wrapper.narrow-scroll-bar'));
    
    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
    const [modalDetails, setModalDetails] = useState<LockerFile>();
    const [showToast, setToast] = useState<boolean>(false);
    
    useEffect(() => {
        !isConfirmationModalOpen && !showToast && setModalDetails(undefined);
    }, [isConfirmationModalOpen]);

    // scroll div is in view, and there is more data to retrieve
    useEffect(()=>{
        isVisible && lockerFiles.length && (lockerFiles.length < total) && getLockerFiles('page');
    },[isVisible, total])


    function getURL(action:any) {
        return `locker/users/${userId}/files${action === 'restore' ? `/${action}` : ''}/${modalDetails?.id}`
    }       

    function handleModalConfirm(): void{
        const action = modalDetails?.isDeleted ? 'restore' : 'delete'
        restoreOrDelete(getURL(action), action)
            .then(() => {
                getLockerFiles('refresh');
                setConfirmationModalOpen(false);
            })
            .catch(() => {
                setToast(true)
                setConfirmationModalOpen(false);
            })
    }
    
    function handleModalCancel(): void{
        setConfirmationModalOpen(false);
    }

    return (
        <>
            {lockerFiles.length >= 1
                ?   <>
                    <div className="locker-details-table-wrapper narrow-scroll-bar">
                        <table className="locker-file-details-table details-table">
                            <thead>
                                <tr>
                                    <FilterHeader 
                                        columnNames={['Upload Date', 'File Name']} 
                                        setFilter={(filters)=>{setFilterOptions(filters)}}
                                        currentFilters={filterOptions}
                                    />
                                    <th>Status</th>
                                    <th aria-label='Actions'><div style={{minWidth: '29px', minHeight: '29px'}} >&nbsp;</div></th>
                                </tr>
                            </thead>

                            <tbody>
                                {lockerFiles.map((file, idx) => (
                                    <LockerTableRow
                                        key={`lockerfile-detail-row-${idx+ 1}`} {...file}
                                        className={idx % 2 === 1 ? 'odd' : 'even'}
                                        buttonClick= {()=> {
                                            setModalDetails(file)
                                            setConfirmationModalOpen(true);
                                        }}
                                    />
                                ))}
                            </tbody>
                        </table>
                        <div ref={scrollTo}/>
                    </div></>

                : <div className="empty-locker-wrapper">
                    <img src={`${BuildPath()}/images/locker/filenotFound.svg`} alt="no locker files" />
                    <p className="empty-warning">{
                        searchTerms 
                            ? 'This User has no locker files matching your search criteria'
                            : 'This User has no files in their locker'
                    }</p>
                </div>
            
            }
            
        {modalDetails &&
            <CancelConfirmModal
                className={"restore-remove-locker-modal"}
                headerContent={<></>}
                isAlertStyle={!modalDetails.isDeleted}
                bodyContent={
                    <div className="restore-remove-locker-content">
                        Are you sure you want to 
                        {" "}
                        {modalDetails.isDeleted 
                            ? 'restore' 
                            : 'remove'
                        }
                        {" "}
                        <span className={`${modalDetails.isDeleted ? "blue-content-span" : "red-content-span"} content-span`}>
                            {modalDetails.filename}
                        </span>
                    </div>
                }
                onCloseClick={handleModalCancel}
                cancelButtonText={"No, Cancel"}
                cancelButtonAriaLabel={"Cancel"}
                confirmButtonText={`Yes, ${modalDetails.isDeleted ? 'Restore' : 'Remove'}`}
                confirmButtonAriaLabel="Confirm"
                onCancelClick={handleModalCancel}
                onConfirmClick={handleModalConfirm}
            />
        }

        <ToastNotification
            show={showToast}
            closeToast={() => setToast(false)}
            messageType={ToastMessageType.WARNING}
        >
            {modalDetails &&
                `Unfortunately we could not ${modalDetails.isDeleted ? 'restore' : 'remove'} ${modalDetails.filename} at this time. Please try again later.`
            }
        </ToastNotification>
    </>
    )
}

export default LockerFilesTable;